// src/pages/PublisherHomePage.jsx
// Phase P1.5: Publisher Dashboard - Control Panel Layout with Logo Masthead
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PublisherNav from '../components/PublisherNav';
import PublisherIDCard from '../components/PublisherIDCard';
import TTSStatusBanner from '../components/TTSStatusBanner';
import { Building2, Users, BookOpen, BarChart3, CheckCircle, AlertCircle, ArrowRight, Info, ImagePlus, Wrench, Loader2 } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { BACKEND_URL as backendUrl } from '@/config/api';

const PublisherHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [publisherProfile, setPublisherProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorCount, setAuthorCount] = useState(0);
  const [titleCount, setTitleCount] = useState(0);
  const [runningFixes, setRunningFixes] = useState(false);
  const [fixResults, setFixResults] = useState(null);
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchPublisherProfile();
    fetchCounts();
  }, []);

  const fetchPublisherProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${backendUrl}/api/publisher/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists === false) {
          setPublisherProfile(null);
        } else {
          setPublisherProfile(data);
        }
      }
    } catch (error) {
      console.error('Error fetching publisher profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch authors count
      const authorsResponse = await fetch(`${backendUrl}/api/publisher/authors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (authorsResponse.ok) {
        const authorsData = await authorsResponse.json();
        setAuthorCount(authorsData.authors?.length || 0);
      }
      
      // Fetch titles count
      const titlesResponse = await fetch(`${backendUrl}/api/publisher/titles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (titlesResponse.ok) {
        const titlesData = await titlesResponse.json();
        setTitleCount(titlesData.titles?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  // Manual fix trigger for development/debugging
  const runManualFixes = async () => {
    setRunningFixes(true);
    setFixResults(null);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/run-manual-fixes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setFixResults(data);
      console.log('[ManualFixes] Results:', data);
      
      // Refresh counts after fixes
      fetchCounts();
    } catch (error) {
      console.error('[ManualFixes] Error:', error);
      setFixResults({ ok: false, error: error.message });
    } finally {
      setRunningFixes(false);
    }
  };

  const profileComplete = publisherProfile && publisherProfile.legal_business_name;
  const hasLogo = publisherProfile?.imprint_logo_url;
  const logoUrl = hasLogo ? `${backendUrl}${publisherProfile.imprint_logo_url}?t=${publisherProfile.updated_at || Date.now()}` : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      {/* TTS Status Banner - shows when TTS jobs are active */}
      <TTSStatusBanner />
      
      {/* Main Content */}
      <div className="pb-20 md:pb-0">
        {/* Header with Optimized Layout */}
        <header className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-500">
          <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
            {/* Three-column layout: Logo | Welcome | ID Card */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              
              {/* Left: Logo */}
              {profileComplete && (
                <div className="flex-shrink-0">
                  {hasLogo ? (
                    <img
                      src={logoUrl}
                      alt="Publisher Logo"
                      className="w-full sm:w-auto h-auto max-h-20 sm:max-h-24 lg:max-h-28 max-w-full sm:max-w-[280px] lg:max-w-[320px] rounded-lg object-contain bg-white/95 p-2"
                    />
                  ) : (
                    <Link
                      to="/publisher/profile"
                      className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-white/30 bg-white/10 hover:bg-white/20 hover:border-white/50 transition-colors"
                    >
                      <ImagePlus className="w-6 h-6 text-white/70" />
                      <div className="text-left">
                        <p className="text-xs font-medium text-white">Add Logo</p>
                      </div>
                    </Link>
                  )}
                </div>
              )}

              {/* Center: Welcome + Description */}
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base uppercase tracking-[0.18em] text-blue-100/90">
                  Publisher Dashboard
                </p>
                <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                  Welcome{user?.first_name ? `, ${user.first_name}` : ''}
                </h1>
                <p className="mt-2 text-sm text-blue-100/80 max-w-md">
                  Manage your publishing operations, author relationships, and catalog from your command center.
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-blue-900/50 px-2.5 py-0.5 text-xs font-medium text-blue-100 ring-1 ring-blue-200/40">
                    <Building2 className="w-3 h-3 mr-1" />
                    Role: Publisher
                  </span>
                  {profileComplete ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-900/50 px-2.5 py-0.5 text-xs font-medium text-emerald-100 ring-1 ring-emerald-200/40">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Profile Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-amber-900/50 px-2.5 py-0.5 text-xs font-medium text-amber-100 ring-1 ring-amber-200/40">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Profile Incomplete
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Publisher ID Card */}
              {profileComplete && (
                <div className="flex-shrink-0">
                  <PublisherIDCard profile={publisherProfile} loading={loading} compact />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="mx-auto max-w-6xl px-4 py-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
              <p className="mt-4 text-slate-400">Loading...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Profile Setup Card - Only show if profile incomplete */}
              {!profileComplete && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-6">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-amber-500/20 p-3">
                      <AlertCircle className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-amber-200">Complete Your Publisher Profile</h2>
                      <p className="mt-1 text-sm text-amber-300/80">
                        Set up your business information and upload your imprint logo to unlock publisher features.
                      </p>
                      <button
                        onClick={() => navigate('/publisher/profile')}
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 px-6 py-2 text-sm font-semibold text-slate-900 transition-colors"
                      >
                        Complete Profile
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Operational Tiles */}
              <div>
                <h2 className="text-lg font-semibold text-slate-200 mb-4">Publisher Operations</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Manage Authors */}
                  <Link 
                    to="/publisher/authors"
                    className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6 relative overflow-hidden hover:border-blue-500/50 hover:bg-slate-900/70 transition-colors block"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full"></div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="rounded-full bg-blue-600/20 p-3 w-fit">
                        <Users className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="text-right">
                        <span className="text-3xl font-bold text-blue-400">{authorCount}</span>
                        <p className="text-xs text-slate-500">Authors</p>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Manage Authors</h3>
                    <p className="mt-2 text-sm text-slate-400">
                      Invite and manage authors in your publishing roster. Create author profiles for titles you represent.
                    </p>
                    {/* Hierarchy Note */}
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-800/50 p-2 text-xs text-slate-400">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
                      <span>Authors form the foundation of your publishing catalog.</span>
                    </div>
                  </Link>

                  {/* Manage Titles */}
                  <Link 
                    to="/publisher/titles"
                    className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6 relative overflow-hidden hover:border-indigo-500/50 hover:bg-slate-900/70 transition-colors block"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-bl-full"></div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="rounded-full bg-indigo-600/20 p-3 w-fit">
                        <BookOpen className="w-6 h-6 text-indigo-400" />
                      </div>
                      <div className="text-right">
                        <span className="text-3xl font-bold text-indigo-400">{titleCount}</span>
                        <p className="text-xs text-slate-500">Titles</p>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Manage Titles</h3>
                    <p className="mt-2 text-sm text-slate-400">
                      Upload and manage titles for your authors.
                    </p>
                    {/* Hierarchy Note */}
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-800/50 p-2 text-xs text-slate-400">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
                      <span>Titles are managed under Authors. Select an Author to view their titles.</span>
                    </div>
                  </Link>

                  {/* Analytics */}
                  <Link 
                    to="/publisher/analytics"
                    className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6 relative overflow-hidden hover:border-purple-500/50 hover:bg-slate-900/70 transition-colors block"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-transparent rounded-bl-full"></div>
                    <div className="rounded-full bg-purple-600/20 p-3 w-fit mb-4">
                      <BarChart3 className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Analytics</h3>
                    <p className="mt-2 text-sm text-slate-400">
                      View engagement analytics across your entire catalog and author roster.
                    </p>
                    {/* Engagement Note */}
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-800/50 p-2 text-xs text-slate-400">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
                      <span>Engagement-only metrics. What should I do next?</span>
                    </div>
                  </Link>
                </div>
              </div>

              {/* Profile Details Link - Compact, below tiles */}
              {profileComplete && (
                <div className="flex justify-end">
                  <Link
                    to="/publisher/profile"
                    className="text-sm text-slate-400 hover:text-slate-200 underline underline-offset-4"
                  >
                    Manage Publisher Profile →
                  </Link>
                </div>
              )}
              
              {/* Dev Tools - Manual Fix Trigger (DISABLED) */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-slate-600" />
                    <span className="text-sm text-slate-500">Developer Tools (Disabled)</span>
                  </div>
                  <button
                    disabled={true}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-500 cursor-not-allowed opacity-50"
                    data-testid="run-manual-fixes-btn"
                  >
                    <Wrench className="w-4 h-4" />
                    Manual Fixes Disabled
                  </button>
                </div>
                
                {/* Fix Results */}
                {fixResults && (
                  <div className={`p-4 rounded-lg text-sm ${
                    fixResults.ok ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
                  }`}>
                    {fixResults.ok ? (
                      <>
                        <p className="font-medium text-green-400 mb-2">✓ Fixes Completed</p>
                        {fixResults.fixes_run?.map((fix, i) => (
                          <div key={i} className="mb-2 p-2 bg-slate-800/50 rounded">
                            <p className="text-slate-300">
                              <span className="font-medium">{fix.fix}:</span> {fix.status}
                            </p>
                            <p className="text-slate-400 text-xs">{fix.message}</p>
                            {fix.manuscript_id && (
                              <p className="text-blue-400 text-xs">ID: {fix.manuscript_id}</p>
                            )}
                          </div>
                        ))}
                        {fixResults.errors?.length > 0 && (
                          <div className="mt-2 p-2 bg-red-900/30 rounded">
                            <p className="text-red-400 text-xs">Errors: {fixResults.errors.map(e => e.error).join(', ')}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-red-400">Error: {fixResults.error || 'Unknown error'}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
      <UniversalFooter />
    </div>
  );
};

export default PublisherHomePage;
